import { memo, useEffect, useState } from "react";
import { Card, Button, ButtonGroup, Toast, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHeart as solidHeart,
  faHeart as regularHeart,
  faInfoCircle,
  faWarning,
} from "@fortawesome/free-solid-svg-icons";
import PropTypes from "prop-types";

import CopyToClipboardButton from "../CopyToClipboardButton";
import getCoordinates from "../getCoordinates";
import PropertyNotes from "../PropertyNotes";
import { propertiesAPI } from "./PropertiesAPI";
import {
  registrationEndDateToDisplay,
  injectHardCodedRegistrationDates,
  isOnlineOrZeus,
} from "../../utilities/hybridSales";

import RegistrationButton from "./RegistrationButton";
import TimeToggle from "./TimeToggle";

const PropertiesCard = ({
  property,
  favoriteId,
  handleDetailsClick,
  handleRegisterOrUnregisterClick,
  isPropertyFavorite,
  isRegistered,
  getFavoriteButtonLabel,
  getRegisterButtonLabel,
  propIndex,
  originalSaleId,
  userRegistrations,
  removeFavoriteFunc,
  addFavoriteFunc,
}) => {
  const [propertyInfo, setPropertyInfo] = useState(null);
  const [foreclosureSaleId, setForeclosureSaleId] = useState(null);
  const [isFetchingInfo, setIsFetchingInfo] = useState(false);
  const [coordinates, setCoordinates] = useState(property.coordinates);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorToastMessage, setErrorToastMessage] = useState("");
  const [auctionDetail, setAuctionDetail] = useState(
    injectHardCodedRegistrationDates(property.auctionDetail, property.saleId)
  );

  const standardizedSaleId = originalSaleId ?? property.saleId;
  const regEndDate = registrationEndDateToDisplay(auctionDetail);
  const isHybrid = isOnlineOrZeus(property) === "Online Registration/In-Person Sale";

  // --- Fetch propertyInfo on mount ---
  useEffect(() => {
    if (propertyInfo || isFetchingInfo) return;
    if (standardizedSaleId === null || standardizedSaleId === undefined || isNaN(Number(standardizedSaleId))) {
      setErrorToastMessage("Invalid sale ID for this property.");
      setShowErrorToast(true);
      return;
    }

    const fetchInfo = async () => {
      setIsFetchingInfo(true);
      try {
        const details = await propertiesAPI.getPropertyDetail({
          id: property.id,
          propertyId: property.propertyId,
          saleId: Number(standardizedSaleId),
          saleType: property.saleType,
        });
        if (details?.propertyInfo) {
          setPropertyInfo(details.propertyInfo);
        } else {
          setErrorToastMessage("Property details not found.");
          setShowErrorToast(true);
        }
        // Update auctionDetail from the full detail response
        if (details?.auctionDetail) {
          setAuctionDetail(injectHardCodedRegistrationDates(details.auctionDetail, property.saleId));
        }
        // For foreclosures, the real auction sale ID lives in countyDetail.sales
        // since saleId on the property card is always 0
        if (property.saleType === "F" && details?.countyDetail?.sales) {
          const foreclosureSale = details.countyDetail.sales.find(
            (s) => s.saleType === "F"
          );
          if (foreclosureSale?.saleId) {
            setForeclosureSaleId(foreclosureSale.saleId);
          }
        }
      } catch {
        setErrorToastMessage("Failed to fetch property details.");
        setShowErrorToast(true);
      } finally {
        setIsFetchingInfo(false);
      }
    };

    fetchInfo();
  }, [property.id, property.propertyId, property.saleType, standardizedSaleId]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Geocode if coordinates are missing ---
  useEffect(() => {
    if (coordinates?.lat && coordinates?.lng) return;
    if (!property.latitude || !property.longitude) {
      if (property.address1 && property.city && property.state && property.zip) {
        const address = `${property.address1}, ${property.city}, ${property.state} ${property.zip}`;
        getCoordinates(address).then((fetched) => {
          if (fetched?.lat && fetched?.lng) setCoordinates(fetched);
        });
      }
    } else {
      setCoordinates({ lat: property.latitude, lng: property.longitude });
    }
  }, [property, coordinates]);

  const isTaxSale = property.saleTypeDescription !== "Foreclosure";
  const isForeclosureSale = property.saleTypeDescription === "Foreclosure";

  // --- Favorite toggle ---
  // Routes through parent props rather than dispatching to Redux directly,
  // so all error handling and toast logic is centralised in Properties.jsx
  const handleFavoriteClick = () => {
    if (isPropertyFavorite(property)) {
      if (favoriteId && removeFavoriteFunc) removeFavoriteFunc();
    } else {
      if (addFavoriteFunc) addFavoriteFunc();
    }
  };

  return (
    <Card className="property-card" style={{ width: "21rem", minHeight: "400px", margin: "2rem" }}>
      <Card.Body>
        {/* Address */}
        <Card.Title className="card-title">
          <span className="truncate">
            {property.address1}, {property.address2}
          </span>
          <CopyToClipboardButton
            textToCopy={`${property.address1}, ${property.city}, ${property.state} ${property.zip}`}
            className="copy-button"
          />
        </Card.Title>

        {/* City, State, Zip */}
        <Card.Subtitle className="mb-2 text-muted truncate">
          {`${property.city}, ${property.state} ${property.zip}`}
        </Card.Subtitle>

        {/* Hybrid sale warning */}
        {isHybrid && (
          <div
            style={{
              backgroundColor: "#fff3cd",
              border: "1px solid #ffc107",
              borderRadius: "4px",
              padding: "6px 10px",
              marginBottom: "8px",
              fontSize: "12px",
              color: "#664d03",
            }}
          >
            <FontAwesomeIcon icon={faWarning} /> No on-site registration. Registration must be completed online prior to{" "}
            <b>{regEndDate}</b>.
          </div>
        )}

        {/* Tax sale fields */}
        {isTaxSale && (
          <>
            <div className="card-text-row">
              <span className="card-text-label">Sale ID:</span>
              <span className="card-text-value truncate">
                {property.displaySaleId || "N/A"} <CopyToClipboardButton textToCopy={property.displaySaleId || "N/A"} />
              </span>
            </div>
            <div className="card-text-row">
              <span className="card-text-label">Parcel #:</span>
              <div className="card-text-value">
                {property.propertyId ? (
                  property.propertyId.split(" / ").map((parcel, idx) => (
                    <div className="truncate" key={idx}>
                      {parcel} <CopyToClipboardButton textToCopy={parcel} />
                    </div>
                  ))
                ) : (
                  <span>N/A</span>
                )}
              </div>
            </div>
          </>
        )}

        {/* Foreclosure-specific fields */}
        {isForeclosureSale && propertyInfo && (
          <>
            <div className="card-text-row">
              <span className="card-text-label">Cause #:</span>
              <span className="card-text-value truncate">
                {propertyInfo.causeNumber || "N/A"}
                <CopyToClipboardButton textToCopy={propertyInfo.causeNumber || "N/A"} />
              </span>
            </div>
            <div className="card-text-row">
              <span className="card-text-label">Defendant:</span>
              <span className="card-text-value truncate">
                {propertyInfo.defendant || "N/A"} <CopyToClipboardButton textToCopy={propertyInfo.defendant || "N/A"} />
              </span>
            </div>
          </>
        )}

        {/* Sale date */}
        <div className="card-text-row">
          <span className="card-text-label">Sale Date:</span>
          <span className="card-text-value truncate">{property.auctionDate || "N/A"}</span>
        </div>

        {/* Time toggle */}
        <TimeToggle property={property} />

        {/* Status */}
        <div className="card-text-row">
          <span className="card-text-label">Status:</span>
          <span className="card-text-value truncate">{property.saleStatusDescription || "N/A"}</span>
        </div>

        {/* Sale type */}
        <div className="card-text-row">
          <span className="card-text-label">Sale Type:</span>
          <span className="card-text-value truncate">{property.saleTypeDescription || "N/A"}</span>
        </div>

        {/* Method */}
        <div className="card-text-row">
          <span className="card-text-label">Method:</span>
          <span className="card-text-value truncate">{isHybrid ? "In-Person Sale" : property.type || "N/A"}</span>
        </div>

        {/* Notes */}
        <PropertyNotes
          propertyId={property.propertyId}
          state={property.state}
          county={property.county}
          address={property.address1}
          saleTypeDescription={property.saleTypeDescription}
          address1={property.address1}
        />

        {/* Action buttons */}
        <div className="button-group-container">
          <ButtonGroup size="sm" className="w-100">
            <RegistrationButton
              property={property}
              isRegistered={isRegistered}
              standardizedSaleId={standardizedSaleId}
              effectiveSaleId={
                property.saleType === "F"
                  ? (foreclosureSaleId ?? standardizedSaleId)
                  : standardizedSaleId
              }
              auctionDetail={auctionDetail}
              isFetchingInfo={isFetchingInfo || (property.saleType === "F" && !foreclosureSaleId)}
              propertyInfo={propertyInfo}
              onRegisterClick={handleRegisterOrUnregisterClick}
              onError={(msg) => {
                setErrorToastMessage(msg);
                setShowErrorToast(true);
              }}
            />

            <Button
              className="flex-fill"
              variant={isPropertyFavorite(property) ? "success" : "outline-success"}
              onClick={handleFavoriteClick}
              aria-label={isPropertyFavorite(property) ? "Remove from favorites" : "Add to favorites"}
            >
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
                <FontAwesomeIcon icon={isPropertyFavorite(property) ? solidHeart : regularHeart} />
                <span>{isPropertyFavorite(property) ? "Unfavorite" : "Favorite"}</span>
              </div>
            </Button>

            <Button
              className="flex-fill"
              variant="outline-primary"
              onClick={(e) => handleDetailsClick(property, propertyInfo, e)}
              disabled={isFetchingInfo || !propertyInfo}
              aria-label={isFetchingInfo ? "Loading property details" : "View map and property details"}
            >
              {isFetchingInfo ? (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                  <span>Loading...</span>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
                  <FontAwesomeIcon icon={faInfoCircle} />
                  <span>Map/Details</span>
                </div>
              )}
            </Button>
          </ButtonGroup>
        </div>
      </Card.Body>

      {/* Error toast */}
      <Toast
        onClose={() => setShowErrorToast(false)}
        show={showErrorToast}
        delay={5000}
        autohide
        bg="danger"
        style={{ position: "absolute", top: "10px", right: "10px", minWidth: "200px" }}
      >
        <Toast.Header>
          <strong className="me-auto">Error</strong>
        </Toast.Header>
        <Toast.Body>{errorToastMessage}</Toast.Body>
      </Toast>
    </Card>
  );
};

PropertiesCard.propTypes = {
  property: PropTypes.shape({
    id: PropTypes.number,
    propertyId: PropTypes.string.isRequired,
    saleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    address1: PropTypes.string.isRequired,
    city: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    zip: PropTypes.string.isRequired,
    saleTypeDescription: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    date: PropTypes.string,
    time: PropTypes.string,
    county: PropTypes.string,
    auctionDate: PropTypes.string,
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    auctionDetail: PropTypes.object,
    displaySaleId: PropTypes.string,
    saleStatusDescription: PropTypes.string,
  }).isRequired,
  favoriteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleDetailsClick: PropTypes.func.isRequired,
  handleRegisterOrUnregisterClick: PropTypes.func.isRequired,
  isPropertyFavorite: PropTypes.func.isRequired,
  isRegistered: PropTypes.bool.isRequired,
  getFavoriteButtonLabel: PropTypes.func.isRequired,
  getRegisterButtonLabel: PropTypes.func.isRequired,
  propIndex: PropTypes.number.isRequired,
  originalSaleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  userRegistrations: PropTypes.array,
  removeFavoriteFunc: PropTypes.func,
  addFavoriteFunc: PropTypes.func,
};

export default memo(PropertiesCard);